import {
  type ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
  OAuthApi,
} from '@backstage/frontend-plugin-api';

import type {
  Composite,
  CompositeAlias,
  CompositeApi,
  PatchCompositesResult,
} from '../types';
import { Configuration, InternalCompositesApi } from '../composite-api-sdk';

export const compositeApiRef: ApiRef<CompositeApi> = createApiRef<CompositeApi>(
  {
    id: 'plugin.neo4j-composite.service',
  },
);

export class CompositeClient implements CompositeApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly oktaAuthApi: OAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    oktaAuthApi: OAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.oktaAuthApi = options.oktaAuthApi;
  }

  private async buildConfiguration(): Promise<Configuration> {
    const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const oktaToken = await this.oktaAuthApi.getAccessToken();
    return new Configuration({
      basePath: `${proxyBaseUrl}/composite-api`,
      fetchApi: this.fetchApi.fetch,
      headers: {
        'Aura-Portal-Okta-Token': oktaToken,
      },
    });
  }

  private async buildApi(): Promise<InternalCompositesApi> {
    return new InternalCompositesApi(await this.buildConfiguration());
  }

  async listComposites(showDeleted: boolean): Promise<Composite[]> {
    const api = await this.buildApi();
    return api.listCompositeInstancesInternal({ showDeleted });
  }

  async getComposite(id: string): Promise<Composite> {
    const api = await this.buildApi();
    return api.getCompositeInstancePortal({ id });
  }

  async listCompositeAliases(
    id: string,
    showDeleted: boolean,
  ): Promise<CompositeAlias[]> {
    const api = await this.buildApi();
    if ('listAliasesInternal' in api) {
      return (
        api as InternalCompositesApi & {
          listAliasesInternal: (request: {
            id: string;
            showDeleted?: boolean;
          }) => Promise<CompositeAlias[]>;
        }
      ).listAliasesInternal({ id, showDeleted });
    }

    const configuration = await this.buildConfiguration();
    const query = new URLSearchParams();
    if (showDeleted) {
      query.set('showDeleted', 'true');
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    const response = await configuration.fetchApi!(
      `${configuration.basePath}/internal/v1/portal/composites/${encodeURIComponent(id)}/aliases${suffix}`,
      {
        method: 'GET',
        headers: configuration.headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to list composite aliases: ${response.status} ${response.statusText}`,
      );
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      return [];
    }

    return payload as CompositeAlias[];
  }

  async patchCompositeInternal(
    id: string,
    body: { memory?: string; neo4jVersion?: string },
  ): Promise<void> {
    const api = await this.buildApi();
    await api.patchCompositeInstanceInternal({
      id,
      request: body,
    });
  }

  async patchCompositesInternal(body: {
    ids: string[];
    memory?: string;
    neo4jVersion?: string;
  }): Promise<PatchCompositesResult> {
    const api = await this.buildApi();
    const response = await api.patchCompositeInstancesInternal({
      request: body,
    });
    return {
      results: (response.results ?? []).map(result => ({
        id: result.id ?? '',
        success: result.success ?? false,
        error: result.error,
      })),
    };
  }
}
